import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Skeleton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
} from "@mui/material";
import { Pen, Trash2 } from "lucide-react";
import Loader from "./Loader";

export default function RolesTable({
  roles,
  loading,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
  currentUserRole,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDeleteRole, setToDeleteRole] = useState(null);

  const parsePermissions = (perms) => {
    const result = {};
    perms.forEach((perm) => {
      const [module, action] = perm.split(".");
      if (!result[module]) result[module] = [];
      result[module].push(action);
    });
    return result;
  };

  const theme = useTheme();

  const formatSentences = (text = "") =>
    text.charAt(0).toUpperCase() + text.slice(1);

  const actionStyles = theme.actionStyles;

  const renderActionBadge = (action) => {
    const key = action.toLowerCase();
    const style = actionStyles[key] || actionStyles.view;

    return (
      <Chip
        key={action}
        label={formatSentences(action)}
        size="small"
        sx={{
          fontSize: "12px",
          px: 1.2,
          height: 26,
          textTransform: "capitalize",
          borderRadius: "16px",
          backgroundColor: style.bgcolor,
          color: style.color,
          border: style.border,
        }}
      />
    );
  };

  const handleOpenConfirm = (role) => {
    setToDeleteRole(role);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setToDeleteRole(null);
    setConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await onDelete(toDeleteRole);
    } finally {
      handleCloseConfirm();
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead
              sx={{
                bgcolor: "#fafafa",
                "& .MuiTableCell-head": {
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "capitalize",
                },
              }}
            >
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Permissions</TableCell>
                {(canUpdate || canDelete) && (
                  <TableCell align="right">Actions</TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {roles
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((role) => {
                  const permissions = parsePermissions(role.permissions);
                  const isDisabled =
                    role.name === "Super Admin" ||
                    role.name === currentUserRole;

                  return (
                    <TableRow key={role._id}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
                          {role.name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {Object.keys(permissions).map((moduleName) => (
                          <Box key={moduleName} sx={{ mb: 0.5 }}>
                            <Box
                              sx={{
                                fontSize: "11px",
                                fontWeight: 600,
                                opacity: 0.7,
                                mb: 0.3,
                                textTransform: "capitalize",
                              }}
                            >
                              {formatSentences(moduleName)}
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {permissions[moduleName].map((action) => (
                                <span key={`${moduleName}-${action}`}>
                                  {renderActionBadge(action)}
                                </span>
                              ))}
                            </Box>
                          </Box>
                        ))}
                      </TableCell>

                      {(canUpdate || canDelete) && (
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 1,
                            }}
                          >
                            {canUpdate && (
                              <Tooltip
                                title={
                                  isDisabled ? "Action disabled" : "Edit Role"
                                }
                              >
                                <span>
                                  <IconButton
                                    disabled={isDisabled}
                                    sx={{
                                      color: isDisabled
                                        ? theme.palette.text.disabled
                                        : theme.palette.primary.main,
                                    }}
                                    onClick={() => !isDisabled && onEdit(role)}
                                  >
                                    <Pen size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {canDelete && (
                              <Tooltip
                                title={
                                  isDisabled ? "Action disabled" : "Delete Role"
                                }
                              >
                                <span>
                                  <IconButton
                                    disabled={isDisabled}
                                    sx={{
                                      color: isDisabled ? "#999" : "#dc2626",
                                    }}
                                    onClick={() =>
                                      !isDisabled
                                        ? handleOpenConfirm(role)
                                        : null
                                    }
                                  >
                                    <Trash2 size={18} />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={roles.length}
            page={page}
            onPageChange={(e, n) => setPage(n)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 8, 10, 25]}
            labelRowsPerPage="Rows:"
          />
        </TableContainer>
      )}

      {/* Confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        aria-labelledby="confirm-delete-title"
      >
        <DialogTitle id="confirm-delete-title">Confirm delete</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{toDeleteRole?.name || "this role"}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseConfirm} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
